import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useNumberFormat } from "@react-input/number-format";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { twJoin } from "tailwind-merge";

import {
  type DiscountAvailabilityTime,
  type DiscountItem,
  locationDiscountsGetAllByLocationId,
  locationDiscountsUpdate,
} from "~/api-refs";
import {
  AvailabilityTimesFields,
  type AvailabilityTimesForm,
} from "~/components/shared/availability-times-fields";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "~/components/ui/input-group";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/locations/$id/discounts/$discountId/edit"
)({
  component: EditLocationDiscountModal,
});

interface LocationDiscountForm extends AvailabilityTimesForm {
  name: string;
  value: string;
  precision: "percentage" | "amount";
  applyToAlcohol: boolean;
  employeeRestriction: boolean;
  matchBusinessHours: boolean;
}

const DISCOUNT_PRECISIONS = ["percentage", "amount"] as const;
const isDiscountPrecision = enumIncludes(DISCOUNT_PRECISIONS);

function EditLocationDiscountModal() {
  const { id, discountId } = Route.useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  // Fetch discount data
  const { data } = useQuery(
    convexQuery(locationDiscountsGetAllByLocationId, {
      locationId: toConvexTableId("locations", id),
    })
  );

  const restaurantDiscount = data?.discounts?.find(
    (d: DiscountItem) => d._id === discountId
  );

  const restaurantDiscountUpdateMutation = useMutation({
    mutationFn: (params: {
      locationDiscountId: Id<"locationDiscounts">;
      locationId: Id<"locations">;
      data: LocationDiscountForm;
    }) => {
      return convexClient.mutation(locationDiscountsUpdate, params);
    },
    onSuccess: () => {
      ProductEvents.discountUpdated(discountId);
    },
  });

  const form = useForm<LocationDiscountForm>({
    defaultValues: {
      name: "",
      value: "",
      precision: "percentage",
      applyToAlcohol: false,
      employeeRestriction: false,
      matchBusinessHours: true,
      availabilityTimes: [],
    },
  });

  const formWatchPrecision = form.watch("precision");
  const formWatchMatchBusinessHours = form.watch("matchBusinessHours");

  useEffect(() => {
    if (restaurantDiscount) {
      if (restaurantDiscount.matchBusinessHours) {
        form.reset({
          name: restaurantDiscount.name,
          value: String(restaurantDiscount.value),
          precision: parseEnumValue(
            restaurantDiscount.precision,
            isDiscountPrecision,
            "percentage"
          ),
          applyToAlcohol: restaurantDiscount.applyToAlcohol,
          employeeRestriction: restaurantDiscount.employeeRestriction,
          matchBusinessHours: true,
        });
      } else {
        form.reset({
          name: restaurantDiscount.name,
          value: String(restaurantDiscount.value),
          precision: parseEnumValue(
            restaurantDiscount.precision,
            isDiscountPrecision,
            "percentage"
          ),
          applyToAlcohol: restaurantDiscount.applyToAlcohol,
          employeeRestriction: restaurantDiscount.employeeRestriction,
          matchBusinessHours: false,
          availabilityTimes:
            restaurantDiscount.availableTimes?.map(
              (availabilityTime: DiscountAvailabilityTime) => ({
                dayOfTheWeek: availabilityTime.dayOfTheWeek,
                startTime: timeConvert24hrTo12hr(availabilityTime.startTime),
                endTime: timeConvert24hrTo12hr(availabilityTime.endTime),
              })
            ) ?? [],
        });
      }
    }
  }, [restaurantDiscount, form]);

  const priceInputRef = useNumberFormat({
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    maximumIntegerDigits: 3,
    locales: ["en-US"],
  });

  const handleClose = () => {
    form.reset();
    setStep(0);
    void navigate({ to: `/locations/${id}/discounts` });
  };

  const handleSubmit = async (formData: LocationDiscountForm) => {
    try {
      await restaurantDiscountUpdateMutation.mutateAsync({
        locationDiscountId: toConvexTableId("locationDiscounts", discountId),
        locationId: toConvexTableId("locations", id),
        data: formData,
      });
      toast.success("Discount updated", {
        description: `${formData.name} discount has been updated to POS devices at this location.`,
      });
      handleClose();
    } catch {
      toast.error("Error updating discount");
    }
  };

  if (!restaurantDiscount) {
    return (
      <ModalLoadingDialog
        message="Loading discount details..."
        onOpenChange={handleClose}
      />
    );
  }

  const isPending = restaurantDiscountUpdateMutation.isPending;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-2xl space-y-5">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center leading-9">
            {step === 0 ? "Edit Discount" : "Available Times"}
          </DialogTitle>
          <DialogDescription className="body text-foreground text-center">
            {step === 0
              ? "Setup discounts that can be applied to an item or total bill."
              : "Choose when this discount is available in POS."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {step === 0 ? (
              <>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="precision"
                  render={({ field }) => (
                    <div className="flex justify-between space-x-3">
                      <button
                        className={twJoin(
                          "w-full cursor-pointer rounded-lg p-4",
                          field.value === "percentage"
                            ? // vortex-allow-color: deliberate near-black hairline on the discount voucher preview, matches print output
                              "border border-black/80 ring-1 ring-black/80"
                            : "border border-border ring-1 ring-transparent"
                        )}
                        onClick={() => field.onChange("percentage")}
                        type="button"
                      >
                        <p className="subtitle text-foreground text-center">
                          Percentage (%)
                        </p>
                        <p className="body-sm text-foreground text-center leading-tight">
                          Calculated percentage
                        </p>
                      </button>
                      <button
                        className={twJoin(
                          "w-full cursor-pointer rounded-lg p-4",
                          field.value === "amount"
                            ? // vortex-allow-color: deliberate near-black hairline on the discount voucher preview, matches print output
                              "border border-black/80 ring-1 ring-black/80"
                            : "border border-border ring-1 ring-transparent"
                        )}
                        onClick={() => field.onChange("amount")}
                        type="button"
                      >
                        <p className="subtitle text-foreground text-center">
                          Amount ($)
                        </p>
                        <p className="body-sm text-foreground text-center leading-tight">
                          Flat amount
                        </p>
                      </button>
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Value</FormLabel>
                      <FormControl>
                        {formWatchPrecision === "amount" ? (
                          <InputGroup>
                            <InputGroupAddon align="inline-start">
                              <InputGroupText>$</InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                              inputMode="decimal"
                              {...field}
                              placeholder="0.00"
                              ref={priceInputRef}
                              value={field.value ?? ""}
                            />
                          </InputGroup>
                        ) : (
                          <InputGroup>
                            <InputGroupInput
                              inputMode="decimal"
                              {...field}
                              placeholder="0.00"
                              ref={priceInputRef}
                              value={field.value ?? ""}
                            />
                            <InputGroupAddon align="inline-end">
                              <InputGroupText>%</InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="applyToAlcohol"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div>
                        <p className="label text-foreground leading-tight">
                          Apply to Alcohol
                        </p>
                        <p className="body-sm text-muted-foreground leading-tight">
                          Allow this discount to be applied to alcohol items.
                        </p>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="employeeRestriction"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div>
                        <p className="label text-foreground leading-tight">
                          Remove employee restriction
                        </p>
                        <p className="body-sm text-muted-foreground leading-tight">
                          Allow any employee at this location to apply the
                          discount.
                        </p>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="w-full"
                  onClick={() => setStep(1)}
                  type="button"
                >
                  Next
                </Button>
              </>
            ) : null}
            {step === 1 ? (
              <>
                <FormField
                  control={form.control}
                  name="matchBusinessHours"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3">
                      <div className="w-full">
                        <p className="label text-foreground leading-tight">
                          Match business hours
                        </p>
                        <p className="body-sm text-muted-foreground leading-tight">
                          Enable this discount during location business hours
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {formWatchMatchBusinessHours ? null : (
                  <>
                    <div className="border-border h-px w-full border-t" />
                    <fieldset>
                      <legend className="pb-3">
                        <Label asChild>
                          <p>Customise available times</p>
                        </Label>
                        <p className="body-sm text-foreground">
                          This discount can only be applied during the times
                          specified below.
                        </p>
                      </legend>
                      <AvailabilityTimesFields showLabel={false} />
                    </fieldset>
                  </>
                )}
                <div className="flex w-full justify-between space-x-3">
                  <Button
                    className="h-9 w-full"
                    onClick={() => setStep(0)}
                    type="button"
                    variant="outline"
                  >
                    Back
                  </Button>
                  <Button
                    className="h-9 w-full"
                    disabled={isPending}
                    type="submit"
                  >
                    Save Discount
                  </Button>
                </div>
              </>
            ) : null}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
